<template>
  <div
    class="letter-grid-container w-full max-w-[540px] sm:max-w-[640px] md:max-w-[720px] lg:max-w-[800px] mx-auto py-3 px-4"
  >
    <div
      role="grid"
      :aria-label="STRINGS.templates.letterGridLabel(letters.length, 10)"
      class="letter-grid"
    >
      <div
        v-for="(slot, index) in gridSlots"
        :key="index"
        role="gridcell"
        :aria-label="slot.ariaLabel"
        :class="slot.classes"
        class="letter-tile"
      >
        {{ slot.letter }}
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, watch } from 'vue';
import { STRINGS } from '@/constants/strings';
import type { Letter } from '@/types/generated';

interface Props {
  letters: Letter[];
  currentSlot: number;
  animationState?: 'idle' | 'revealing' | 'complete';
}

const props = withDefaults(defineProps<Props>(), {
  animationState: 'idle',
});

// Create 10 slots with letters or empty state
const gridSlots = computed(() => {
  return Array.from({ length: 10 }, (_, index) => {
    const slotNumber = index + 1;
    const letter = props.letters[index];
    const isEmpty = !letter;
    const isRevealing = props.currentSlot === slotNumber && props.animationState === 'revealing';

    return {
      letter: isEmpty ? '' : letter.char,
      ariaLabel: STRINGS.templates.letterPosition(slotNumber, isEmpty ? undefined : letter.char),
      classes: {
        'letter-tile--empty': isEmpty,
        'letter-tile--filled': !isEmpty,
        'letter-tile--revealing': isRevealing,
      },
    };
  });
});

// Watch for new letters and trigger reveal animation
watch(
  () => props.letters.length,
  (newLength, oldLength) => {
    if (newLength > oldLength && props.animationState === 'revealing') {
      // Animation will be handled by CSS class
    }
  }
);
</script>

<style scoped>
.letter-tile {
  animation: tileEntrance 0.3s ease-out both;
  transition: transform 150ms cubic-bezier(0, 0, 0.2, 1);
}

.letter-tile--filled {
  animation: none;
}

.letter-tile--filled:hover {
  transform: translateY(-2px) perspective(400px) rotateX(4deg);
}

.letter-tile:nth-child(1) {
  animation-delay: 0ms;
}
.letter-tile:nth-child(2) {
  animation-delay: 60ms;
}
.letter-tile:nth-child(3) {
  animation-delay: 120ms;
}
.letter-tile:nth-child(4) {
  animation-delay: 180ms;
}
.letter-tile:nth-child(5) {
  animation-delay: 240ms;
}
.letter-tile:nth-child(6) {
  animation-delay: 300ms;
}
.letter-tile:nth-child(7) {
  animation-delay: 360ms;
}
.letter-tile:nth-child(8) {
  animation-delay: 420ms;
}
.letter-tile:nth-child(9) {
  animation-delay: 480ms;
}
.letter-tile:nth-child(10) {
  animation-delay: 540ms;
}

@keyframes tileEntrance {
  from {
    opacity: 0;
    transform: translateY(8px) scale(0.9);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.letter-tile--revealing {
  animation: letterReveal 0.4s cubic-bezier(0.68, -0.55, 0.265, 1.55) forwards !important;
}

@keyframes letterReveal {
  0% {
    transform: perspective(200px) rotateX(80deg) translateY(24px) scale(0.5);
    opacity: 0;
  }
  12% {
    opacity: 0.9;
  }
  40% {
    transform: perspective(200px) rotateX(-18deg) translateY(-12px) scale(1.1);
    opacity: 1;
  }
  60% {
    transform: perspective(200px) rotateX(8deg) translateY(5px) scale(1.02);
    opacity: 1;
  }
  78% {
    transform: perspective(200px) rotateX(-3deg) translateY(-2px) scale(1.01);
    opacity: 1;
  }
  100% {
    transform: perspective(200px) rotateX(0deg) translateY(0) scale(1);
    opacity: 1;
  }
}

@media (prefers-reduced-motion: reduce) {
  .letter-tile {
    animation: none !important;
  }

  .letter-tile--revealing {
    animation: letterRevealReduced 200ms ease-out !important;
  }

  .letter-tile--filled:hover {
    transform: none;
  }

  @keyframes letterRevealReduced {
    0% {
      opacity: 0;
    }
    100% {
      opacity: 1;
    }
  }
}
</style>
